<template>
    <v-card flat>
        <v-card-text class="page--status_reports">
          <h2 class="page-title">Status Reports (Weekly)</h2>
          <p class="text-xs-center"><em>{{ lastChecked }}</em></p>
          <v-layout class="status_reports mt-4" v-if="!!$roomService.rooms">
            <v-flex>
              <v-container class="top-container top-container--full" grid-list-md>
                <v-layout row wrap justify-space-around>
                  <v-flex lg4 md4 sm12 xs12>
                    <v-text-field
                      append-icon="search"
                      label="Rooms"
                      single-line
                      hide-details
                      v-model="rawSearchTerms"
                      :loading="$reportService.loading"
                    ></v-text-field>
                  </v-flex>

                  <v-flex lg4 md4 sm12 xs12 v-if="showDepartmentUnit">
                    <v-select
                        class="select__room_org_units"
                        v-bind:items="roomOrgUnits"
                        v-model="filter.roomOrgUnit"
                        item-text="label"
                        item-value="value"
                        label="Department / Unit"
                        :loading="$reportService.loading"
                        autocomplete
                        clearable
                    ></v-select>
                  </v-flex>

                  <v-flex lg4 md4 sm12 xs12>
                    <v-select
                        class="select__room_owners"
                        v-bind:items="roomOwners"
                        v-model="filter.roomOwner"
                        item-text="label"
                        item-value="value"
                        label="Owner"
                        :loading="$reportService.loading"
                        autocomplete
                        clearable
                    ></v-select>
                  </v-flex>

                  <v-flex lg6 md6 sm6 xs12>
                    <v-checkbox
                      class="mx-auto"
                      label="Only Show Overdue Rooms"
                      v-model="filter.onlyOverdue"
                      color="primary"
                    ></v-checkbox>
                  </v-flex>

                  <v-flex v-if="viewerIsRoomOwner" lg6 md6 sm6 xs12 class="text-sm-right text-xs-center">
                    <v-btn
                      class="btn__my_rooms"
                      color="primary"
                      @click="filter.roomOwner = $auth.user().uid"
                    >
                      <span class="btn__text">Show My Rooms</span>
                    </v-btn>
                  </v-flex>


                </v-layout>
              </v-container>

              <v-data-table
                v-bind:search="filter.searchTerms"
                v-bind:headers="reportsTableHeaders"
                v-bind:items="reportsTableItems"
                v-bind:pagination.sync="pagination"
                :custom-filter="customSearchFilter"
                :loading="$reportService.loading"
                item-key="room_id"
                must-sort
                hide-actions
                no-results-text="Sorry, either there are no results available that match the search criteria, or data is not currently available."
                no-data-text="Sorry, either there are no results available that match the search criteria, or data is not currently available."
              >
                <template slot="headers" slot-scope="props">
                  <tr class="primary">
                    <template v-for="header in props.headers">
                      <th
                        :key="header.text"
                        v-if="!header.hide"
                        role="columnheader"
                        :aria-label="header.text"
                        :aria-sort="header.sortable ? 'sortable' : ''"
                        @click="changeSort(header)"
                        :class="[
                          (header.sortable || header.align !== 'left') ? 'px-1' : 'pr-1',
                          'white--text',
                          'text-xs-' + (header.align ? header.align : 'center'),
                          'column',
                          header.isWeekHeader ? 'column--room_status' : '',
                          'column--' + header.value,
                          header.value === pagination.sortBy ? 'active' : '',
                          header.sortable !== false ? 'sortable' : '',
                          pagination.descending ? 'desc' : 'asc'
                        ]">
                          <v-icon v-if="header.sortable !== false" color="white">arrow_upward</v-icon>
                          {{ header.text }}
                        </th>
                    </template>
                  </tr>
                </template>
                <template slot="items" slot-scope="props">
                  <td class="text-xs-left column--room_report px-1 text-xs-center">
                    <v-layout row>
                      <v-flex xs6>
                        <v-btn
                          :class="'ma-0'"
                          color="grey"
                          flat
                          depressed
                          small
                          fab
                          :disabled="roomReportDialog"
                          @click="openRoomReport(props.item.room_id)"
                        >
                          <v-icon>assignment</v-icon>
                        </v-btn>
                      </v-flex>
                      <v-flex xs6>
                        <v-btn
                          :class="'ma-0'"
                          color="grey"
                          flat
                          depressed
                          small
                          fab
                          @click="startReport(props.item.room_id)"
                        >
                          <v-icon>edit</v-icon>
                        </v-btn>
                      </v-flex>
                    </v-layout>
                  </td>
                  <td class="text-xs-left column--room_label pr-1">
                    {{ props.item.label }}
                  </td>
                  <td class="text-xs-left column--org_unit pr-1" v-if="showDepartmentUnit">
                    <div
                      v-if="!!props.item.org_unit.line1"
                      class="org_unit__line1"
                    >
                      {{ props.item.org_unit.line1 }}
                    </div>
                    <div
                      v-if="!!props.item.org_unit.line2"
                      class="org_unit__line2"
                    >
                      {{ props.item.org_unit.line2 }}
                    </div>
                  </td>
                  <td class="text-xs-left column--campus pr-1">
                    {{ props.item.campus }}
                  </td>
                  <td class="text-xs-left column--campus_region pr-1">
                    {{ props.item.campus_region }}
                  </td>
                  <td
                    :class="[
                      'text-xs-right',
                      'column--room_minimum_frequency',
                      'px-1',
                      props.item.check_status_color_class
                    ]"
                  >
                    <v-tooltip right v-if="!!props.item.support_level">
                      <div slot="activator">{{ props.item.minimum_frequency }} Days</div>
                      <span>{{ props.item.support_level }}</span>
                    </v-tooltip>
                    <span v-else>{{ props.item.minimum_frequency }} Days</span>
                  </td>
                  <td class="text-xs-left column--room_last_checked pr-1">{{ props.item.last_checked | date }}</td>
                  <template v-for="(week, index) in weeks">
                    <td v-bind:key="index" class="text-xs-center px-1 column--room_status">
                      <v-icon
                        :color="getStatusIcon(props.item['room_status_' + week]).color"
                      >{{ getStatusIcon(props.item['room_status_' + week]).icon }}</v-icon>
                    </td>
                  </template>
                </template>
              </v-data-table>
              <v-dialog
                v-model="roomReportDialog"
                persistent
                scrollable
                max-width="90%"
                content-class="dialog--room_status_reports"
              >
                <v-card>
                  <v-toolbar dark color="primary">
                    <v-toolbar-title>{{ roomReportLabel }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click.native="closeRoomReport()" dark>
                      <v-icon>close</v-icon>
                    </v-btn>
                  </v-toolbar>
                  <v-data-table
                    v-bind:headers="roomReportTableHeaders"
                    v-bind:items="roomReportTableItems"
                    :loading="$reportService.loading"
                    item-key="report_id"
                    hide-actions
                    no-results-text="No reports are available for this room."
                    no-data-text="No reports are available for this room."
                  >
                    <template slot="items" slot-scope="props">
                      <td class="text-xs-center column--room_status" >
                        <v-icon
                          :color="getStatusIcon(props.item.room_status).color"
                        >{{ getStatusIcon(props.item.room_status).icon }}</v-icon>
                      </td>
                      <td class="text-xs-left column--date">{{ props.item.timestamp | date }}</td>
                      <td class="text-xs-left column--notes">
                        <ul class="feature_bullet_list">
                          <template v-for="(feature, index) in props.item.inventory">
                            <li
                              v-bind:key="index"
                              :class="'feature_bullet_list_item feature_bullet_list_item--' + getStatusIcon(props.item[feature.featureId]).color"
                            >
                              <strong class="feature_bullet__name">
                                {{ feature.featureName }}
                              </strong>
                              <em
                                v-if="props.item[feature.featureId + '_issue_description']"
                                class="feature_bullet__notes"
                              >
                                {{ props.item[feature.featureId + '_issue_description'] }}
                              </em>
                            </li>
                          </template>
                        </ul>
                      </td>
                    </template>
                  </v-data-table>
                </v-card>
              </v-dialog>
            </v-flex>
          </v-layout>
        </v-card-text>
    </v-card>
</template>

<style>
td.column--room_report {
  width: 96px;
}
td.column--room_label {
  min-width: 140px;
}
td.column--org_unit {
  min-width: 90px;
}
td.column--room_minimum_frequency {
  min-width: 50px;
}
td.column--room_last_checked {
  min-width: 105px;
}
table.table thead th.column--room_status,
table.table tbody td.column--room_status {
  min-width: 50px;
  max-width: 50px;
  white-space: normal;
}
table.table thead th.column--room_status {
  border-left: 1px solid rgba(131, 110, 170, .5);;
}
/* table.table thead th.column--room_status:nth-child(2n) {
  background-color: #401f68;
} */
table.table tbody td.column--room_status:nth-child(2n) {
  background-color: rgba(131, 110, 170, .07);
  border-left: 1px solid transparent;
}
.dialog--room_status_reports tr th:nth-child(2),
.dialog--room_status_reports tr td:nth-child(2) {
  width: 130px;
}

div.flex {
  max-width: 100%;
}

.top-container {
  max-width: 600px;
}

.top-container--full {
  max-width: 100%;
}

</style>

<script>
// TODO: Make whole room clickable to trigger roomReport
import moment from 'moment-business-days'

export default {
  name: 'StatusReports',
  data() {
    return {
      rawSearchTerms: '',
      searchTimeout: -1,
      debouncePeriod: 500,
      searchKeys: ['active_status', 'campus', 'campus_region', 'customer_or_owner', 'email_primary_owner', 'email_secondary_owner', 'label', 'netid_primary_owner', 'primary_contact_name', 'room_type', 'secondary_contact_name', 'secondary_customer_or_owner'],
      filter: {
        searchTerms: '',
        roomOwner: '',
        roomOrgUnit: '',
        onlyOverdue: false
      },
      pagination: {
        sortBy: 'last_checked',
        descending: true,
        rowsPerPage: -1,
        page: 1
      },
      roomReportDialog: false,
      roomReportRoomId: null,
      now: Date.now(),
    };
  },
  props: {
    teamId: {
      type: String,
      required: true
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    startReport(roomId) {
      const teamId = this.teamId;
      this.$router.push({
        path: `/${teamId}/report/${roomId}`
      });
    },
    loadData() {
      this.$reportService.refreshData(this.teamId);
      this.$roomService.refreshData(this.teamId);
    },
    customSearchFilter (items, search, filter) {
      search = search.toString().toLowerCase();

      if(this.filter.onlyOverdue) {
        items = items.filter(it => !!it.check_overdue);
      }

      // Apply organizational owner filter
      if(this.filter.roomOrgUnit) {
        items = items.filter(it =>
          it.org_unit.line1 === this.filter.roomOrgUnit || it.org_unit.value === this.filter.roomOrgUnit
        );
      }

      // Apply room owner filter
      if(this.filter.roomOwner) {
        items = items.filter(it =>
          it.netid_primary_owner === this.filter.roomOwner || it.netid_secondary_owner === this.filter.roomOwner
        );
      }

      // Apply keyword search filters
      if(search) {
        // Split the search terms up by space, and search for all of them
        var searchTerms = search.split(" ")
        items = items.filter(i => searchTerms.every(term => this.searchKeys.some(j => filter(i[j], term))))
      }

      return items
    },
    changeSort (header) {
      if(header.sortable !== false) {
        var column = header.value;
        if (this.pagination.sortBy === column) {
          this.pagination.descending = !this.pagination.descending
        } else {
          this.pagination.sortBy = column
          this.pagination.descending = false
        }
      }
    },
    openRoomReport(roomId) {
      this.roomReportRoomId = roomId;
      this.roomReportDialog = true;
    },
    closeRoomReport() {
      // this.roomReportRoomId = null; // TODO: Maybe re-enable this?
      this.roomReportDialog = false;
    },
    getStatusIcon(status) {
      if(status === true) {
        return {
          color: 'success',
          icon: 'check_circle'
        }
      } else if(status === false) {
        return {
          color: 'warning',
          icon: 'warning'
        }
      } else {
        return {
          color: 'grey lighten-1',
          icon: 'do_not_disturb_on'
        }
      }
    },
    finishSearchDebounce () {
      this.filter.searchTerms = this.rawSearchTerms
    }
  },
  computed: {
    weeks() {
      if(this.$reportService.reports) {
        return Array.from(new Set(this.$reportService.reports.filter(it => !!it.date_year_week).map(it => it.date_year_week))).sort().reverse();
      }
      return [];
    },
    reportsTableHeaders() {
      var headers = [
        {
          text: 'Actions',
          align: 'left',
          sortable: false
        }, {
          text: 'Room',
          align: 'left',
          value: 'label'
        }, {
          text: 'Dept. / Unit',
          align: 'left',
          value: 'org_unit.value',
          sortable: true,
          hide: !this.showDepartmentUnit
        }, {
          text: 'Campus',
          align: 'left',
          value: 'campus',
          sortable: true
        }, {
          text: 'Region',
          align: 'left',
          value: 'campus_region',
          sortable: true
        }, {
          text: 'Frequency',
          align: 'right',
          value: 'minimum_frequency',
          sortable: true
        }, {
          text: 'Last Checked',
          align: 'left',
          value: 'last_checked'
        }
      ];
      if(this.weeks) {
        headers = headers.concat(
          this.weeks.map(week => {
            return {
              text: moment(week, 'YYYYWW').startOf('week').format('M/D') + ' - ' + moment(week, 'YYYYWW').endOf('week').format('M/D'),
              value: `room_status_` + week,
              isWeekHeader: true,
              align: 'center',
              sortable: false
            }
          })
        );
      }
      return headers;
    },
    reportsTableItems() {
      var rooms = this.$roomService.rooms;
      var reports = this.$reportService.reports;

      if(rooms && reports) {
        rooms = rooms.map(room => {
          // Check for and mark whether rooms are overdue
          var business_days_since_check = moment(room.last_checked, 'x').endOf('day').businessDiff(moment().endOf('day'));
          room.check_status_color_class = '';
          if(business_days_since_check > room.minimum_frequency || !room.last_checked) {
            room.check_status_color_class = 'error--text';
            room.check_overdue = true;
          } else {
            room.check_overdue = false;
          }
          // Uncomment lines below to color code checks due today as yellow
          // else if(business_days_since_check == room.minimum_frequency) {
          //   room.check_status_color_class = 'warning--text'
          //   room.check_overdue = false;
          // }

          // Insert report status for each week into the room object
          reports.filter(it => it.room_id === room.room_id && !!it.date_year_week).forEach(report => {
            room['room_status_' + report.date_year_week] = report.room_status;
          })

          // Add data for department / unit column
          room.org_unit = {
            value: '',
            line1: '',
            line2: ''
          };
          if(!!room.customer_or_owner) {
            room.org_unit.line1 = room.customer_or_owner;
            if(!!room.secondary_customer_or_owner) {
              room.org_unit.line2 = room.secondary_customer_or_owner;
            }
            room.org_unit.value = `${room.org_unit.line1} - ${room.org_unit.line2}`;
          }

          return room;
        });

        return rooms;
      }

      return [];
    },
    roomReportLabel() {
      if(this.roomReportRoomId) {
        return this.$roomService.getRoom(this.roomReportRoomId).label
      }
      return ''
    },
    roomReportTableHeaders() {
      if(this.roomReportRoomId) {
        var headers = [
          {
            text: "Status",
            align: "center",
            sortable: false,
            value: "room_status",
          },
          {
            text: "Date",
            align: "left",
            sortable: false,
            value: "timestamp",
          },
          {
            text: "Notes",
            align: "left",
            sortable: false,
            value: "room_status",
          }
        ];
        return headers;
      }
      return [];
    },
    roomReportTableItems() {
      if(this.roomReportRoomId) {
        return this.$reportService.reports.filter(it => it.room_id == this.roomReportRoomId).reverse().map(report => {
          report.inventory = this.$roomService.getRoomInventory(report.room_id).filter(feature => report[feature.featureId] === false || report[feature.featureId + '_issue_description']);
          if(!report.inventory.length) {
            report.clean_report = true;
            report.clean_report_issue_description = '';
            report.inventory.push({
              featureId: 'clean_report',
              featureName: 'No Issues Reported',
            });
          }
          return report;
        });
      }
      return [];
    },
    roomOwners() {
      var rooms = this.$roomService.rooms;
      var owners = Array.from(new Set(
        rooms.filter(it => !!it.netid_primary_owner && !!it.primary_contact_name).map(it => {
          return {
            value: it.netid_primary_owner,
            label: it.primary_contact_name
          }
        }).concat(rooms.filter(it => !!it.netid_secondary_owner && !!it.secondary_contact_name).map(it => {
          return {
            value: it.netid_secondary_owner,
            label: it.secondary_contact_name
          }
        }))
      ));
      return owners
    },
    roomOrgUnits() {
      var rooms = this.$roomService.rooms;
      var orgUnits = Array.from(new Set(
        rooms
          .filter(it => !!it.customer_or_owner)
          .map(it => it.customer_or_owner)
          .concat(
            rooms
              .filter(it => !!it.customer_or_owner && !!it.secondary_customer_or_owner)
              .map(it => it.customer_or_owner + " - " + it.secondary_customer_or_owner)
          )
      )).sort();
      return orgUnits
    },
    viewerIsRoomOwner() {
      return !!this.roomOwners.filter(it => it.value == this.$auth.user().uid).length
    },
    showDepartmentUnit() {
      return !!this.roomOrgUnits.length
    },
    lastChecked() {
      var prefix = 'Last Updated: ';
      if(this.$roomService.rooms) {
        var lastRoomsChecked = this.$roomService.rooms.filter(it => !!it.last_checked).map(it => it.last_checked).sort().reverse();
        if(lastRoomsChecked.length) {
          return prefix + moment(lastRoomsChecked[0], 'x').format('M/D/YY') + ' at ' + moment(lastRoomsChecked[0], 'x').format('h:mma')
        }
        return prefix + 'Never';
      }
      return 'Loading...';
    }
  },
  watch: {
    rawSearchTerms () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(this.finishSearchDebounce, this.debouncePeriod)
    }
  },
  filters: {
    date(timestamp) {
      return timestamp ? moment(timestamp, 'x').format('MM/DD/YYYY h:mma') : 'Never';
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/StatusReports.vue